import styles from './EmptyState.module.css';

import type { FC, ReactNode } from 'react';
import type { ColorVariant } from 'src/theme';

import React from 'react';

import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';

/** What the icon says before the words do: a fact (neutral), a warning, or a fault. */
export type EmptyStateTone = 'neutral' | 'warning' | 'danger';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

interface EmptyStateProps {
  /** A Phosphor icon element; the colour is applied here, by tone. */
  icon: ReactNode;
  title: string;
  description?: string;
  /** The one action of the screen — the only place the accent appears. */
  action?: EmptyStateAction;
  tone?: EmptyStateTone;
  /**
   * `h1` where this state stands *instead of* the screen's own title — a listing that is gone, a
   * deal that did not load, an address with nothing at it — so the screen still has a heading
   * (app#242). Everywhere else the state sits under the screen's `h1` and its title stays a `div`.
   */
  titleAs?: 'h1';
}

const ICON_COLOR: Record<EmptyStateTone, ColorVariant> = {
  neutral: 'mutedWhite',
  warning: 'warning',
  danger: 'danger',
};

/**
 * The one shape every "nothing to show" screen takes: an icon, a title, one line, at most one
 * action. Used for a feed with no cars, a filter with no matches, a listing that is gone, a lost
 * connection, a broken api and an address with nothing at it — six screens that used to be six
 * inline blocks or, worse, blank (storefront-fix-spec §PR-4).
 *
 * The brand rules it carries: the accent sits on the single action and nowhere else, status
 * colours mark the icon and never fill a block, no illustration, no gradient (brand.md, "Состав
 * после чистки").
 */
export const EmptyState: FC<EmptyStateProps> = (props) => {
  const { icon, title, description, action, tone = 'neutral', titleAs } = props;

  return (
    <Stack
      gap={3}
      alignItems="center"
      p={4}
      mt={8}
      width="100%"
      className={styles.container}
      role={tone === 'neutral' ? undefined : 'alert'}
    >
      <Typography color={ICON_COLOR[tone]} className={styles.icon}>
        {icon}
      </Typography>
      <Typography as={titleAs} variant="title3" color="white" textAlign="center">
        {title}
      </Typography>
      {description ? (
        <Typography color="lightWhite" textAlign="center" className={styles.description}>
          {description}
        </Typography>
      ) : null}
      {action ? (
        <Button
          onClick={action.onClick}
          title={action.label}
          variant="accent"
          textColor="accentInk"
          className={styles.action}
        />
      ) : null}
    </Stack>
  );
};
